/* eslint-disable no-irregular-whitespace */
import { bytesToNDigits } from '@mail/cross-sizes-utils';
import { Icon16Done } from '@vkontakte/icons';
import { Spacing } from '@vkontakte/vkui';
import promoImg from 'img/safe/safePromoLight.png';
import React from 'react';
import { helpSafeHref } from 'reactApp/appHelpers/featuresHelpers/features/overquotaSafe';
import { renderCommonWhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog.helpers';
import { isDarkThemeModeEnabled } from 'reactApp/utils/theme';

import styles from './SafePromoDialog.css';
import { type VideoCoverImg, isVideoCover, safePromoDialogCoverConfig, SafePromoType, sendSafeModalDwh } from './SafePromoDialog.helpers';

interface Args {
    promoType: SafePromoType;
    safeSize?: number;

    onClickRestore?(): void;

    onClickClearSafe?(): void;
}

const getTitle = (promoType: SafePromoType, safeSize: number) => {
    if (promoType === SafePromoType.afterMoveTrashbinFiles && safeSize) {
        return `${bytesToNDigits(safeSize, 3).value} файлов из корзины теперь в сейфе — место в Облаке освободилось`;
    }

    if (promoType === SafePromoType.afterMoveCleanerFiles && safeSize) {
        return `${bytesToNDigits(safeSize, 3).value} файлов теперь в сейфе. Почему это удобно`;
    }

    if (promoType === SafePromoType.afterMoveCleanerLetters && safeSize) {
        return `${bytesToNDigits(safeSize, 3).value} писем теперь в сейфе. Почему это удобно`;
    }

    if (promoType === SafePromoType.moreAboutSafe) {
        return `Сейф — пространство для файлов, которое не расходует место в Облаке`;
    }

    if (promoType === SafePromoType.manage) {
        return `Файлы из сейфа не занимают место в Облаке`;
    }

    return 'Ваши файлы будут в сейфе';
};

const features: Record<SafePromoType, string[] | string> = {
    [SafePromoType.base]: [
        'Сейф не расходует место в Облаке — можно сохранить всё, что лежит в корзине',
        'Верните файлы, когда будет нужно — сразу все. Им понадобится свободное место',
        'В сейфе поместится 1 ТБ файлов на срок до 3 месяцев',
    ],
    [SafePromoType.afterMoveTrashbinFiles]: [
        'Сейф — пространство для файлов, которое не занимает в Облаке ни килобайта',
        'Если в файлах было что‑то важное — можно вернуть сразу все. Они снова займут место в Облаке',
        'В сейфе поместится 1 ТБ файлов на срок до 3 месяцев',
    ],
    [SafePromoType.afterMoveCleanerFiles]: [
        'Сейф — папка для файлов, которая не расходует место в едином пространстве Почты и Облака',
        'Файлы можно вернуть, если они вам понадобятся',
        'В сейф поместится 1 ТБ файлов на срок до 3 месяцев',
    ],
    [SafePromoType.afterMoveCleanerLetters]: [
        'Сейф — папка для писем, которая не расходует место в едином пространстве Почты и Облака',
        'Письма можно вернуть, если они вам понадобятся',
        'В сейф поместится 1 ТБ писем на срок до 3 месяцев',
    ],
    [SafePromoType.moreAboutSafe]: [
        'В сейф перемещаются файлы из корзины — до 1 ТБ.',
        'Положите лишнее в сейф, если Облако переполнено — это может помочь временно снять ограничения',
        'Файлы хранятся в сейфе 3 месяца, затем вернутся в Облако, сразу все. Но можно вернуть и раньше',
    ],
    [SafePromoType.manage]: 'Их можно вернуть — если в сейф попали нужные фото, видео или документы',
};

export const renderSafePromoDialog = (
    { promoType = SafePromoType.base, safeSize = 0, onClickClearSafe, onClickRestore }: Args = {
        promoType: SafePromoType.base,
        safeSize: 0,
    }
) => {
    const isDarkTheme = isDarkThemeModeEnabled();

    const coverUrl =
        (isDarkTheme ? safePromoDialogCoverConfig.dark[promoType] : safePromoDialogCoverConfig.light[promoType]) || (promoImg as string);
    const isVideo = isVideoCover(promoType);

    const featuresData = features[promoType];

    return renderCommonWhatsNewDialog({
        qaId: `safe-promo-dialog:${promoType}`,
        title: <p className={styles.title}>{getTitle(promoType, safeSize)}</p>,
        content: (
            <>
                <Spacing size={12} />
                <div className={styles.items}>
                    {Array.isArray(featuresData) ? (
                        featuresData.map((text, idx) => (
                            <div key={idx} className={styles.item}>
                                <div className={styles.icon}>
                                    <Icon16Done fill="var(--vkui--color_background_accent)" width={16} height={16} />
                                </div>
                                <span className={styles.text}>{text}</span>
                            </div>
                        ))
                    ) : (
                        <div className={styles.item}>
                            <span className={styles.text}>{featuresData}</span>
                        </div>
                    )}
                </div>
                <Spacing size={24} />
            </>
        ),
        primaryButtonText: promoType === SafePromoType.manage ? 'Вернуть файлы в Облако' : 'Понятно',
        secondaryButtonText: promoType === SafePromoType.manage ? 'Очистить сейф' : 'Узнать больше о сейфе',

        imageUrl: !isVideo ? (coverUrl as string) : '',
        videoUrl: isVideo ? (coverUrl as VideoCoverImg) : undefined,
        hideImage: isVideo,
        showVideo: isVideo,
        loopVideo: isVideo,

        onClick: () => {
            sendSafeModalDwh({ action: 'click', buttonName: 'ok', promoType });

            if (promoType === SafePromoType.manage) {
                onClickRestore?.();
            }
        },
        onDismiss: () => {
            sendSafeModalDwh({ action: 'close', promoType });
        },
        onSecondary: () => {
            sendSafeModalDwh({ action: 'click', buttonName: 'more', promoType });

            if (promoType === SafePromoType.manage) {
                onClickClearSafe?.();
            } else {
                window.open(helpSafeHref, '_blank', 'noopener noreferrer');
            }
        },
        onShow: () => {
            sendSafeModalDwh({ action: 'show', promoType });
        },
        dialogSize: 'tiny',
        buttonTheme: 'vk',
        className: styles.root,
        contentClassName: styles.content,
        imageClassName: styles.image,
        closeAfterClick: true,
    });
};
